import { useMemo, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { EmojiPickerField } from "@/components/EmojiPickerField";
import { SyncBranchModal } from "@/components/SyncBranchModal";
import {
  ArrowDown,
  ArrowUp,
  Clock,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import type { PageProps, Service, ServiceCategory } from "@/types";

interface ServicesIndexProps extends PageProps {
  services: Service[];
  can_create?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  service_categories?: ServiceCategory[];
}

function formatPrice(cents: number, currency: string): string {
  const amount = cents / 100;
  if (currency === "MXN") {
    return `$${amount.toLocaleString("es-MX")} MXN`;
  }
  return `$${amount.toFixed(2)} ${currency}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}

export default function ServicesIndex({
  services,
  can_create = true,
  can_edit = true,
  can_delete = true,
  service_categories: initialCategories = [],
  current_user,
  current_organization,
  current_business,
  flash,
}: ServicesIndexProps) {
  const orgSlug = current_organization?.slug || "";
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);
  const [categories, setCategories] = useState(initialCategories);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryEmoji, setNewCategoryEmoji] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null,
  );
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingCategoryEmoji, setEditingCategoryEmoji] = useState("");
  const [savingCategoryId, setSavingCategoryId] = useState<number | null>(null);
  const [categoryError, setCategoryError] = useState("");
  const [serviceQuery, setServiceQuery] = useState("");
  const visibleServices = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();
    if (!q) return services;
    return services.filter((service) => service.name.toLowerCase().includes(q));
  }, [services, serviceQuery]);

  const orderedCategories = [...categories].sort(
    (left, right) =>
      left.position - right.position || left.name.localeCompare(right.name),
  );

  const csrfToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const createCategory = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingCategory(true);
    setCategoryError("");
    try {
      const response = await fetch(`/${orgSlug}/service_categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          service_category: {
            name: newCategoryName,
            emoji: newCategoryEmoji || null,
            position:
              Math.max(-1, ...categories.map((category) => category.position)) +
              1,
            active: true,
          },
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setCategoryError(
          Object.values(payload.errors || {}).flat().join(", ") ||
            "No se pudo crear la categoría.",
        );
        return;
      }
      setCategories((current) => [...current, payload.service_category]);
      setNewCategoryName("");
      setNewCategoryEmoji("");
    } catch {
      setCategoryError("No se pudo crear la categoría.");
    } finally {
      setSavingCategory(false);
    }
  };

  const updateCategory = async (
    category: ServiceCategory,
    changes: Partial<ServiceCategory>,
  ) => {
    const response = await fetch(
      `/${orgSlug}/service_categories/${category.id}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ service_category: changes }),
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(
        Object.values(payload.errors || {}).flat().join(", ") ||
          "No se pudo actualizar la categoría.",
      );
    }
    return payload.service_category as ServiceCategory;
  };

  const startEditingCategory = (category: ServiceCategory) => {
    setCategoryError("");
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setEditingCategoryEmoji(category.emoji || "");
  };

  const cancelEditingCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
    setEditingCategoryEmoji("");
  };

  const saveCategory = async (
    event: React.FormEvent,
    category: ServiceCategory,
  ) => {
    event.preventDefault();
    const name = editingCategoryName.trim();
    if (!name) return;

    setSavingCategoryId(category.id);
    setCategoryError("");
    try {
      const updated = await updateCategory(category, {
        name,
        emoji: editingCategoryEmoji || null,
      });
      setCategories((items) =>
        items.map((item) => (item.id === updated.id ? updated : item)),
      );
      cancelEditingCategory();
    } catch (error) {
      setCategoryError(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar la categoría.",
      );
    } finally {
      setSavingCategoryId(null);
    }
  };

  const moveCategory = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= orderedCategories.length) return;
    setCategoryError("");
    const current = orderedCategories[index];
    const target = orderedCategories[targetIndex];
    try {
      const [updatedCurrent, updatedTarget] = await Promise.all([
        updateCategory(current, { position: targetIndex }),
        updateCategory(target, { position: index }),
      ]);
      setCategories((items) =>
        items.map((item) => {
          if (item.id === updatedCurrent.id) return updatedCurrent;
          if (item.id === updatedTarget.id) return updatedTarget;
          return item;
        }),
      );
    } catch (error) {
      setCategoryError(
        error instanceof Error
          ? error.message
          : "No se pudo reordenar la categoría.",
      );
    }
  };

  const toggleCategory = async (category: ServiceCategory) => {
    setCategoryError("");
    try {
      const updated = await updateCategory(category, {
        active: !category.active,
      });
      setCategories((items) =>
        items.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setCategoryError(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar la categoría.",
      );
    }
  };

  const retireCategory = async (category: ServiceCategory) => {
    if (
      !confirm(
        `¿Retirar "${category.name}"? Sus servicios pasarán a Otros y no se eliminarán.`,
      )
    )
      return;
    setCategoryError("");
    const response = await fetch(
      `/${orgSlug}/service_categories/${category.id}`,
      {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken },
      },
    );
    if (!response.ok) {
      setCategoryError("No se pudo retirar la categoría.");
      return;
    }
    setCategories((items) => items.filter((item) => item.id !== category.id));
    router.reload({ only: ["services", "service_categories"] });
  };

  const handleDelete = (service: Service) => {
    if (service.sibling_count > 0) {
      setDeleteTarget(service);
      return;
    }
    if (confirm(`¿Estás seguro de eliminar "${service.name}"?`)) {
      router.delete(`/${orgSlug}/services/${service.id}`);
    }
  };

  const confirmDelete = (syncAll: boolean) => {
    if (!deleteTarget) return;
    router.delete(`/${orgSlug}/services/${deleteTarget.id}`, {
      data: { sync_all: syncAll },
    });
    setDeleteTarget(null);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Servicios
            </h1>
            <p className="text-muted-foreground">
              Gestiona los servicios que ofreces
            </p>
          </div>
          {can_create && (
            <Link
              href={`/${orgSlug}/services/new`}
              className="w-full sm:w-auto"
            >
              <Button className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Nuevo servicio
              </Button>
            </Link>
          )}
        </div>

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        {flash?.alert && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {flash.alert}
          </div>
        )}

        {can_create && (
          <section className="rounded-xl border border-border bg-card p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="font-semibold">Categorías del catálogo</h2>
              <p className="text-sm text-muted-foreground">
                WhatsApp muestra primero estas categorías cuando el catálogo no
                cabe en una sola lista. Usa las flechas para definir su orden.
              </p>
            </div>
            {categoryError && (
              <div
                role="alert"
                className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {categoryError}
              </div>
            )}
            {orderedCategories.length > 0 && (
              <div className="mb-4 divide-y divide-border rounded-lg border border-border">
                {orderedCategories.map((category, index) => (
                  <div
                    key={category.id}
                    className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                  >
                    {editingCategoryId === category.id ? (
                      <form
                        className="grid w-full gap-2 sm:grid-cols-[180px_minmax(0,1fr)_auto]"
                        onSubmit={(event) => saveCategory(event, category)}
                      >
                        <EmojiPickerField
                          value={editingCategoryEmoji}
                          onChange={setEditingCategoryEmoji}
                          ariaLabel={`Emoji de ${category.name}`}
                        />
                        <input
                          className="h-10 min-w-0 rounded-md border border-input bg-background px-3 text-sm"
                          aria-label={`Nombre de ${category.name}`}
                          value={editingCategoryName}
                          onChange={(event) =>
                            setEditingCategoryName(event.target.value)
                          }
                          autoFocus
                          required
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="submit"
                            size="sm"
                            disabled={
                              savingCategoryId === category.id ||
                              !editingCategoryName.trim()
                            }
                          >
                            {savingCategoryId === category.id
                              ? "Guardando..."
                              : "Guardar"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={savingCategoryId === category.id}
                            onClick={cancelEditingCategory}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="w-6 text-center">
                            {category.emoji || "•"}
                          </span>
                          <span
                            className={
                              category.active
                                ? "truncate text-sm font-medium"
                                : "truncate text-sm text-muted-foreground line-through"
                            }
                          >
                            {category.name}
                          </span>
                          {!category.active && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                              Oculta
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 self-end sm:self-auto">
                          <span className="mr-1 text-xs text-muted-foreground">
                            Orden
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`Subir ${category.name}`}
                            title="Subir"
                            disabled={index === 0}
                            onClick={() => moveCategory(index, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`Bajar ${category.name}`}
                            title="Bajar"
                            disabled={index === orderedCategories.length - 1}
                            onClick={() => moveCategory(index, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Editar ${category.name}`}
                            title="Editar"
                            onClick={() => startEditingCategory(category)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={
                              category.active
                                ? `Ocultar ${category.name}`
                                : `Mostrar ${category.name}`
                            }
                            title={category.active ? "Ocultar" : "Mostrar"}
                            onClick={() => toggleCategory(category)}
                          >
                            {category.active ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Retirar ${category.name}`}
                            title="Retirar"
                            onClick={() => retireCategory(category)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <form
              onSubmit={createCategory}
              className="grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)_auto]"
            >
              <EmojiPickerField
                value={newCategoryEmoji}
                onChange={setNewCategoryEmoji}
                ariaLabel="Emoji de la nueva categoría"
              />
              <input
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                placeholder="Nueva categoría"
                value={newCategoryName}
                onChange={(event) => setNewCategoryName(event.target.value)}
                required
              />
              <Button type="submit" disabled={savingCategory}>
                {savingCategory ? "Guardando..." : "Agregar"}
              </Button>
            </form>
          </section>
        )}

        <input
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Buscar servicio..."
          value={serviceQuery}
          onChange={(event) => setServiceQuery(event.target.value)}
          aria-label="Buscar servicios"
        />

        {visibleServices.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <p className="text-muted-foreground">
              {serviceQuery ? "No se encontraron servicios" : "No tienes servicios registrados"}
            </p>
            {!serviceQuery && (
            <Link
              href={`/${orgSlug}/services/new`}
              className="mt-4 inline-block"
            >
              <Button variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Crear tu primer servicio
              </Button>
            </Link>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card">
            <div className="divide-y divide-border">
              {visibleServices.map((service) => (
                <div
                  key={service.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <Link
                    href={`/${orgSlug}/services/${service.id}`}
                    className="flex min-w-0 items-center gap-4 rounded-lg hover:bg-accent/50"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted text-lg">
                      {service.emoji}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-card-foreground truncate">
                          {service.name}
                        </h3>
                        {service.service_category_id && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                            {categories.find(
                              (category) =>
                                category.id === service.service_category_id,
                            )?.name || "Categoría"}
                          </span>
                        )}
                        {!service.active && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                          {formatDuration(service.duration_minutes)}
                        </span>
                        <span>
                          {formatPrice(service.price_cents, service.currency)}
                        </span>
                      </div>
                    </div>
                  </Link>
                  {(can_edit || can_delete) && (
                    <div className="flex items-center gap-2 pl-14 sm:pl-0">
                      {can_edit && (
                        <Link href={`/${orgSlug}/services/${service.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                      {can_delete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(service)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SyncBranchModal
        open={!!deleteTarget}
        title="Eliminar servicio"
        description={`"${deleteTarget?.name}" existe en ${deleteTarget?.sibling_count ?? 0} sucursal(es) más. ¿Deseas eliminarlo de todas?`}
        confirmLabel="Eliminar de todas las sucursales"
        singleLabel="Solo de esta sucursal"
        onSyncAll={() => confirmDelete(true)}
        onSingleOnly={() => confirmDelete(false)}
        onCancel={() => setDeleteTarget(null)}
      />
    </DashboardLayout>
  );
}
